// ─────────────────────────────────────────────────────────────────────────────
// BEHAVIORAL FIT ASSESSMENT — Question Bank
// 32 original Likert items (0=Strongly Disagree → 4=Strongly Agree), 8 per
// competency. Built for pre-employment screening across four named
// competencies, each independently scored 0-100, with a role-customisable
// weighted overall fit score.
// ─────────────────────────────────────────────────────────────────────────────

export const BF_ITEMS = [
  // ── LEADERSHIP & TEAM ORIENTATION ────────────────────────────────────────
  { id:"bf01", dim:"LTO", q:"I naturally step forward to coordinate a group when no one else takes charge." },
  { id:"bf02", dim:"LTO", q:"I make an effort to recognise and celebrate my teammates' contributions." },
  { id:"bf03", dim:"LTO", q:"I am comfortable making a decision on behalf of a group when time is short." },
  { id:"bf04", dim:"LTO", q:"I actively help team members who are struggling with their workload." },
  { id:"bf05", dim:"LTO", q:"I look for opportunities to develop the skills of people I work with." },
  { id:"bf06", dim:"LTO", q:"I take responsibility for outcomes even when a team decision doesn't go as planned." },
  { id:"bf07", dim:"LTO", q:"I involve the right people before making decisions that affect the team." },
  { id:"bf08", dim:"LTO", q:"I keep the team informed about progress and changes that affect their work." },

  // ── COMMUNICATION SKILLS ─────────────────────────────────────────────────
  { id:"bf09", dim:"COM", q:"I adjust how I explain something depending on who I'm speaking to." },
  { id:"bf10", dim:"COM", q:"I listen fully before responding, rather than preparing my reply while others speak." },
  { id:"bf11", dim:"COM", q:"I communicate instructions clearly enough that people rarely need to ask me to repeat or clarify them." },
  { id:"bf12", dim:"COM", q:"I ask clarifying questions when instructions are unclear, rather than guessing." },
  { id:"bf13", dim:"COM", q:"I can summarise a complex topic in a few clear sentences." },
  { id:"bf14", dim:"COM", q:"I give feedback in a way that the other person can act on constructively." },
  { id:"bf15", dim:"COM", q:"I keep others updated proactively, rather than waiting to be asked." },
  { id:"bf16", dim:"COM", q:"I express disagreement respectfully rather than avoiding it." },

  // ── STRESS TOLERANCE / PRESSURE HANDLING ─────────────────────────────────
  { id:"bf17", dim:"STP", q:"I stay focused on the task even when a deadline is tight." },
  { id:"bf18", dim:"STP", q:"Unexpected setbacks don't throw off my composure for long." },
  { id:"bf19", dim:"STP", q:"I can think clearly during a high-pressure situation." },
  { id:"bf20", dim:"STP", q:"I don't take frustration out on others when things go wrong." },
  { id:"bf21", dim:"STP", q:"I recover quickly after a stressful day and don't carry it into the next." },
  { id:"bf22", dim:"STP", q:"I remain polite and professional even with a difficult customer or colleague." },
  { id:"bf23", dim:"STP", q:"I can juggle multiple urgent demands without becoming overwhelmed." },
  { id:"bf24", dim:"STP", q:"I stay calm when receiving unexpected criticism." },

  // ── ATTENTION TO DETAIL ──────────────────────────────────────────────────
  { id:"bf25", dim:"ATD", q:"I double-check my work before considering it complete." },
  { id:"bf26", dim:"ATD", q:"I usually catch small errors in my own work before finalising it." },
  { id:"bf27", dim:"ATD", q:"I follow instructions and procedures precisely, even repetitive ones." },
  { id:"bf28", dim:"ATD", q:"I notice inconsistencies in data or documents that others might miss." },
  { id:"bf29", dim:"ATD", q:"I keep my work organised so nothing gets accidentally overlooked." },
  { id:"bf30", dim:"ATD", q:"I prefer to verify something rather than assume it is correct." },
  { id:"bf31", dim:"ATD", q:"I catch my own mistakes before someone else has to point them out." },
  { id:"bf32", dim:"ATD", q:"I am comfortable with detailed, meticulous work, even when it is repetitive." },
];

export const BF_DIMS = {
  LTO: { id:"LTO", label:"Leadership & Team Orientation", icon:"👥", color:"#7c3aed",
         desc:"Willingness to take initiative, support teammates, and share responsibility for group outcomes." },
  COM: { id:"COM", label:"Communication Skills", icon:"💬", color:"#1d4ed8",
         desc:"Clarity, active listening, and the ability to adapt a message to the audience and situation." },
  STP: { id:"STP", label:"Stress Tolerance / Pressure Handling", icon:"🧘", color:"#059669",
         desc:"Composure, clear thinking, and professionalism under deadline pressure or difficult interactions." },
  ATD: { id:"ATD", label:"Attention to Detail", icon:"🔍", color:"#d97706",
         desc:"Precision, thoroughness, and a habit of verifying rather than assuming." },
};

// Same shape/parallel convention used by EQ's DOMAINS — needed by the
// generic InstrumentSurvey completion screen.
export const BF_SCALES = Object.values(BF_DIMS).map(d => ({ id: d.id, label: d.label }));

// ── Role-customisable overall-fit weighting ──────────────────────────────
// An admin selects a role template when reviewing a candidate; the same
// four competency scores combine differently depending on what actually
// matters most for that role. "custom" lets an admin set their own weights
// per requisition without needing a new instrument variant.
export const BF_ROLE_WEIGHTS = {
  balanced:    { label:"General / Balanced",         LTO:0.25, COM:0.25, STP:0.25, ATD:0.25 },
  sales:       { label:"Sales & Business Development", LTO:0.20, COM:0.35, STP:0.30, ATD:0.15 },
  support:     { label:"Customer Support",             LTO:0.10, COM:0.35, STP:0.35, ATD:0.20 },
  operations:  { label:"Operations / Back-Office",     LTO:0.15, COM:0.20, STP:0.25, ATD:0.40 },
  supervisor:  { label:"Team Lead / Supervisor",       LTO:0.35, COM:0.30, STP:0.20, ATD:0.15 },
};
